// Carry Roll LoadDemoModal — dataset picker (Stage 10 load mechanism).
//
// Lists the pre-built demo datasets by name + description. Selecting one applies
// its store snapshot immediately via `loadDataset` and navigates to the
// calculator so the user sees the loaded result. Mirrors the FeedbackForm modal
// styling (CSS tokens + .panel/.btn classes) so it matches the dark theme.

import { useEffect, useRef } from 'react';
import { Database, X } from 'lucide-react';
import { DEMO_DATASETS, type DemoDataset } from '../../data/demoDatasets';
import { useStore } from '../../store';
import { useUI } from '../../store/uiStore';
import { goNav } from '../../lib/nav';

export function LoadDemoModal() {
  const isOpen = useUI((s) => s.demoPickerOpen);
  const onClose = useUI((s) => s.closeDemoPicker);
  const loadDataset = useStore((s) => s.loadDataset);
  const contentRef = useRef<HTMLDivElement>(null);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSelect = (dataset: DemoDataset) => {
    loadDataset(dataset);
    onClose();
    goNav('calc');
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ backgroundColor: 'var(--alert-overlay, rgba(0,0,0,0.65))', zIndex: 50 }}
      role="dialog"
      aria-modal="true"
      aria-label="Load demo dataset"
      onClick={onClose}
    >
      <div
        ref={contentRef}
        tabIndex={-1}
        className="panel w-full max-w-xl overflow-y-auto"
        style={{
          maxHeight: '90vh',
          outline: 'none',
          margin: 0,
          boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="load-demo-modal"
      >
        <div
          className="flex items-center justify-between"
          style={{ marginBottom: 'var(--space-4)' }}
        >
          <h2
            style={{
              fontSize: 'var(--fs-xl)',
              fontWeight: 'var(--fw-bold)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
            }}
          >
            <Database size={20} aria-hidden="true" /> Load demo
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="btn"
            aria-label="Close"
            style={{ padding: 'var(--space-2)' }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <p className="text-sm" style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>
          Load a pre-built dataset to explore the calculator with realistic values. This replaces the current inputs.
        </p>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}>
          {DEMO_DATASETS.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                className="btn w-full"
                style={{
                  textAlign: 'left',
                  display: 'block',
                  padding: 'var(--space-3)',
                }}
                onClick={() => handleSelect(d)}
                data-testid={`load-demo-option-${d.id}`}
                aria-label={`Load ${d.name}`}
              >
                <span style={{ display: 'block', fontWeight: 'var(--fw-bold)' }}>
                  {d.name}
                </span>
                <span style={{ display: 'block', color: 'var(--color-text-muted)', marginTop: '2px' }}>
                  {d.description}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
